import '../community.scss';

import React, {
  memo,
  useCallback,
} from 'react';

import {
  Badge,
  Collapse,
  Drawer,
} from 'antd';
import { BsCaretRightFill } from 'react-icons/bs';
import { HiLockClosed } from 'react-icons/hi2';
import {
  PiArrowUpRightLight,
  PiChatsCircleFill,
  PiCompassFill,
  PiEyeSlash,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';

import tmLogo from '../../../assets/svg/TagMangoWithoutBorder.svg';
import {
  setActiveSection,
  setSelectedChannelData,
} from '../../../context/communityReducer';
import { isChannelLocked } from '../../../shared/communityUtils';
import {
  RouteTitle,
  routeVsTitle,
} from '../../../shared/constants';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../shared/hooks';
import { checkTQConnectAccess } from '../../../shared/tqConnectUtils';
import {
  IChannel,
  IChannelGroup,
  ILink,
} from '../../../types/communityTypes';
import { ROUTES } from '../../../types/routes';
import { Onboarding } from '../../../types/userTypes';
import TQConnectMenuItems
  from '../../TQConnect/components/TQConnectMenuItems/TQConnectMenuItems';

type Props = {
  open: boolean;
  onClose: () => void;
};

const CommunityChannelDrawer: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    activeSection,
    groupChannelList,
    communityLinkList,
    metadata,
    userCommunityAccess: { hasCommunityCreatorAccess },
    // selectedChannelData,
  } = useAppSelector((state) => state.community);

  const userDetails = useAppSelector((state) => state?.user);

  const { isTQConnectEnabled } = checkTQConnectAccess();

  const handleSelectSection = useCallback(
    (
      section: string,
      channelData: IChannel | { name: string },
      url: string,
      isChannelSelected = false,
    ) => {
      dispatch(
        setActiveSection({
          activeSection: section,
          headerTitle: channelData?.name,
        }),
      );
      if (isChannelSelected) {
        // const isLocked = isChannelLocked(
        //   (channelData as IChannel)?.mangoArr || [],
        //   (channelData as IChannel)?.badgeArr || [],
        // );

        dispatch(setSelectedChannelData({ ...channelData } as IChannel));
      } else {
        dispatch(setSelectedChannelData(null));
      }
      navigate(url);
      onClose();
    },
    [dispatch, navigate, onClose],
  );

  const displayChannel = useCallback(
    (channelData: IChannel) => {
      const isLocked = isChannelLocked(
        channelData.mangoArr,
        channelData.badgeArr,
      );
      const feedUnreadCount = metadata[channelData?._id]?.feedUnreadCount;
      return (
        <button
          className={`community-drawer_static-list_button ${
            activeSection === channelData?._id
              ? 'community-drawer_static-list_active-section'
              : ''
          }`}
          key={channelData?._id}
          onClick={() => {
            // setActiveKeyFunc(channelData?.groupArr);

            handleSelectSection(
              channelData?._id,
              channelData,
              ROUTES.COMMUNITY_CHANNEL.replace(':channelId', channelData?._id),
              true,
            );
          }}>
          <div className="community-drawer_static-list_button_wrapper">
            {channelData?.communityProfilePictureType === 'emoji' ? (
              <div className="community-drawer_static-list_button_wrapper_svg-wrapper">
                <div className="community-drawer_static-list_button_wrapper_svg-wrapper_emoji">
                  {channelData?.communityProfilePicture}
                </div>
              </div>
            ) : (
              <div className="community-drawer_static-list_button_wrapper_img-wrapper">
                <img src={channelData?.communityProfilePicture} alt="logo" />
              </div>
            )}
            {channelData?.name}
          </div>
          <div className="community-drawer_static-list_button_lock-wrapper">
            {channelData?.visibility === 'hidden' ? (
              <div className="community-drawer_static-list_button_lock-wrapper_icon">
                <PiEyeSlash />
              </div>
            ) : null}
            {isLocked ? (
              <div className="community-drawer_static-list_button_lock">
                <HiLockClosed />
              </div>
            ) : null}
            {!isLocked && feedUnreadCount ? (
              <Badge count={feedUnreadCount} />
            ) : null}
          </div>
        </button>
      );
    },
    [activeSection, handleSelectSection, metadata],
  );

  const handleLinkClick = (link: string) => {
    window.open(link);
  };

  const displayLink = useCallback((linkData: ILink) => {
    if (!(linkData?.link && linkData?.title)) return null;
    return (
      <button
        className={`community-drawer_static-list_button `}
        key={linkData?._id}
        onClick={() => handleLinkClick(linkData?.link)}>
        <div className="community-drawer_static-list_button_link-wrapper">
          <div className="community-drawer_static-list_button_link-wrapper_logo">
            <PiArrowUpRightLight />
          </div>
          {linkData?.title}
        </div>
      </button>
    );
  }, []);

  const displayChannelList = useCallback(
    (groupData: IChannelGroup) => {
      if (!(groupData?.channelArr?.length && groupData?.name)) return null;
      const showUnread = groupData?.channelArr?.some((each) => {
        const channelMetadata = metadata[each?._id];
        const isLocked = isChannelLocked(
          each?.mangoArr || [],
          each?.badgeArr || [],
        );
        return (
          channelMetadata?.feedUnreadCount > 0 &&
          each?.visibility !== 'hidden' &&
          !isLocked
        );
      });
      return (
        <>
          <Collapse.Panel
            header={
              <>
                <Badge dot={showUnread} style={{ marginRight: -10 }}>
                  {groupData?.name}
                </Badge>
              </>
            }
            key={groupData?._id}>
            {(groupData?.channelArr || []).map((each) => displayChannel(each))}
          </Collapse.Panel>
        </>
      );
    },
    [displayChannel, metadata],
  );

  const displayLinkList = useCallback(
    (LinkList: ILink[]) => {
      return (
        <Collapse.Panel header={'Link'} key={'link'}>
          {LinkList.map((linkData) => displayLink(linkData))}
        </Collapse.Panel>
      );
    },
    [displayLink],
  );

  const displayGroupList = useCallback(() => {
    return (
      <Collapse
        // onChange={(data) => console.log(data)}
        expandIcon={() => <BsCaretRightFill />}>
        {groupChannelList.map((each) => displayChannelList(each))}
        {displayLinkList(communityLinkList)}
      </Collapse>
    );
  }, [
    communityLinkList,
    displayChannelList,
    displayLinkList,
    groupChannelList,
  ]);

  // useEffect(() => {
  //   if (selectedChannelData?._id) {
  //     setActiveKey((prev) => [...prev, selectedChannelData?.groupArr]);
  //   } else {
  //     setActiveKey([]);
  //   }
  // }, [selectedChannelData]);

  return (
    <Drawer
      title=""
      placement="left"
      onClose={onClose}
      open={open}
      width={'100%'}>
      <div className="community-drawer">
        <div className="community-drawer_static-list">
          {userDetails?.type === Onboarding.FAN_COMPLETED ? null : (
            <button
              className={`community-drawer_static-list_button ${
                activeSection === 'tagmango-team'
                  ? 'community-drawer_static-list_active-section'
                  : ''
              }`}
              key="tagmango-team"
              onClick={() =>
                handleSelectSection(
                  'tagmango-team',
                  { name: routeVsTitle[RouteTitle.TM_TEAM] },
                  ROUTES.COMMUNITY_TAGMANGO_TEAM,
                )
              }>
              <div className="community-drawer_static-list_button_wrapper">
                <div className="community-drawer_static-list_button_wrapper_icon-wrapper">
                  <img src={tmLogo} alt="tmlogo" />
                </div>
                {routeVsTitle[RouteTitle.TM_TEAM]}
              </div>
            </button>
          )}

          <button
            className={`community-drawer_static-list_button ${
              activeSection === 'feed'
                ? 'community-drawer_static-list_active-section'
                : ''
            }`}
            key="feed"
            onClick={() =>
              handleSelectSection(
                'feed',
                { name: routeVsTitle.feed },
                ROUTES.COMMUNITY_FEED,
              )
            }>
            <div className="community-drawer_static-list_button_wrapper">
              <div className="community-drawer_static-list_button_wrapper_svg-wrapper">
                <PiCompassFill />
              </div>
              {routeVsTitle.feed}
            </div>
          </button>

          <button
            className={`community-drawer_static-list_button ${
              activeSection === 'messages'
                ? 'community-drawer_static-list_active-section'
                : ''
            }`}
            key="messages"
            onClick={() =>
              handleSelectSection(
                'messages',
                { name: routeVsTitle.messages },
                ROUTES.COMMUNITY_MESSAGES,
              )
            }>
            <div className="community-drawer_static-list_button_wrapper">
              <div className="community-drawer_static-list_button_wrapper_svg-wrapper">
                <PiChatsCircleFill />
              </div>
              {routeVsTitle.messages}
            </div>
          </button>
        </div>

        <div className="community-drawer_dynamic-list">
          {isTQConnectEnabled ? (
            <TQConnectMenuItems
              userAccessType={
                hasCommunityCreatorAccess ? 'creator' : 'customer'
              }
              handleSelectSection={handleSelectSection}
            />
          ) : null}
          {displayGroupList()}
        </div>

        <div className="community-drawer_empty-space" />
      </div>
    </Drawer>
  );
};

export default memo(CommunityChannelDrawer);
